import { useEffect, useMemo } from 'react'

import { FilterOptions } from 'modules/core/components/Filter/filters/AddFilter'

import { useAvailableFilters, useVisibleFilters } from './context'
import {
  FILTER_CONFIGS,
  FilterConfigProps,
  FilterConfigType,
  createFilterConfig,
} from './filterConfig'

interface UseFilterManagerOptions {
  configType: FilterConfigType
  filterProps?: FilterConfigProps
  searchConfig?: {
    search: string
    onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
    placeholder: string
    collapsable?: boolean
  }
}

export const useFilterManager = ({
  configType,
  filterProps,
  searchConfig,
}: UseFilterManagerOptions) => {
  const { visibleFilters, addVisibleFilter } = useVisibleFilters()
  const { availableFilters, setAvailableFilters } = useAvailableFilters()

  const filterConfig = useMemo(() => createFilterConfig(filterProps ?? {}), [filterProps])
  const config = FILTER_CONFIGS[configType]

  const availableConditionalFilterKeys = useMemo(() => {
    return config.conditionalFilterKeys.filter((key) => filterConfig[key])
  }, [config.conditionalFilterKeys, filterConfig])

  useEffect(() => {
    const filterOptions: FilterOptions = availableConditionalFilterKeys.map((key) => ({
      label: filterConfig[key]?.label || key,
      value: key,
    }))
    setAvailableFilters(filterOptions)
  }, [availableConditionalFilterKeys, filterConfig, setAvailableFilters])

  const persistentFilters = useMemo(() => {
    return config.persistentFilterKeys.map((key) => filterConfig[key]?.component()).filter(Boolean)
  }, [config.persistentFilterKeys, filterConfig])

  const conditionalFilters = useMemo(() => {
    return availableConditionalFilterKeys
      .map((key) => (visibleFilters?.includes(key) ? filterConfig[key]?.component() : null))
      .filter(Boolean)
  }, [availableConditionalFilterKeys, visibleFilters, filterConfig])

  const filteredAvailableFilters = useMemo(() => {
    return availableFilters.filter((filter) => !visibleFilters?.includes(filter.value))
  }, [availableFilters, visibleFilters])

  return {
    persistentFilters,
    conditionalFilters,
    availableFilters: filteredAvailableFilters,
    onSelectFilter: addVisibleFilter,
    searchConfig,
  }
}
